import { aiUsagePageTurnsHref } from '@/dashboard/admin/scenes/AiUsage/aiUsageLabels';
import Link from '@/dashboard/components/Link';
import Spinner from '@/dashboard/components/Spinner';
import { Button } from '@/dashboard/components/ui/button';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';
import { useAdminPageAiStatus } from '@/dashboard/hooks/queries/useAdminQueries';
import useSellerQueries from '@/dashboard/hooks/queries/useSellerQueries';
import { adminSellerKey } from '@/dashboard/lib/queryKeys';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { X } from 'lucide-react';
import React, { useState } from 'react';
import { useRouteMatch } from 'react-router-dom';

const OBJECT_ID = /^[a-f\d]{24}$/i;

const aiStatusLabel = ({ createdWithAi, editedWithAi }) => {
  if (createdWithAi && editedWithAi) return 'Created + edited with AI';
  if (createdWithAi) return 'Created with AI';
  if (editedWithAi) return 'Edited with AI';
  return null;
};

function PageAiStatus({ pageId, status }) {
  const label = aiStatusLabel(status);

  if (!label) return null;

  return (
    <Link
      to={aiUsagePageTurnsHref(status.sellerId, pageId)}
      target="_blank"
      rel="noopener noreferrer"
      className="whitespace-nowrap font-medium underline underline-offset-2"
    >
      {label}
    </Link>
  );
}

export default function AdminGhostingDialog({ sellerId }) {
  const {
    isAdminLoggedInAsSeller,
    logAdminInAsSeller,
    userSellerId,
    seller,
    isAdmin,
    availableStores,
  } = useAuth();
  const { getSeller } = useSellerQueries();
  const [isOpen, setIsOpen] = useState(true);

  const routePageId = useRouteMatch('/pages/:id')?.params.id;
  const pageId = OBJECT_ID.test(routePageId) ? routePageId : undefined;
  const isAiEditor = !!useRouteMatch('/pages/:id/customize/ai');

  const isOwnStore =
    !isAdminLoggedInAsSeller &&
    (userSellerId === sellerId || availableStores?.some(store => store._id === sellerId));
  const isVisible = isAdmin && isOpen && !isOwnStore && (isAdminLoggedInAsSeller || !!sellerId);

  const { data: sellerData, isLoading } = useQuery({
    queryKey: adminSellerKey(sellerId),
    queryFn: () => getSeller(sellerId),
    enabled: isVisible && !isAdminLoggedInAsSeller,
  });
  const { data: aiStatus } = useAdminPageAiStatus(pageId, { enabled: isVisible && !!pageId });

  if (!isVisible) return null;

  const store = isAdminLoggedInAsSeller ? seller : sellerData;
  const sellerName = store?.displayName || store?.name;

  return (
    <div
      className={clsx(
        'fixed inset-x-0 z-50 flex justify-center px-3 pointer-events-none',
        // At the bottom it would cover the AI editor's Send button.
        isAiEditor ? 'top-3' : 'bottom-3',
      )}
    >
      <div className="flex min-w-56 max-w-full flex-col gap-y-1.5 rounded-lg border border-foreground bg-white py-1.5 pl-2.5 pr-1.5 text-xs shadow-lg pointer-events-auto dark:bg-background">
        <div className="flex items-center gap-x-2">
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            <span className="min-w-0 flex-1 truncate font-semibold" title={sellerName}>
              {sellerName}
            </span>
          )}
          <Button
            variant="ghost"
            size="icon-xs"
            className="ml-auto"
            aria-label="Close"
            onClick={() => setIsOpen(false)}
          >
            <X />
          </Button>
        </div>

        <div className="flex items-center gap-x-2">
          {aiStatus && <PageAiStatus pageId={pageId} status={aiStatus} />}

          <div className="ml-auto flex gap-x-1.5">
            {isAdminLoggedInAsSeller ? (
              <Button variant="outline" size="xs" onClick={() => logAdminInAsSeller(userSellerId)}>
                Log out
              </Button>
            ) : (
              <Button size="xs" onClick={() => logAdminInAsSeller(sellerId)}>
                Log in
              </Button>
            )}

            <Button variant="outline" size="xs" href={`/admin/merchants/${sellerId}`}>
              Admin
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
